import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MAX_TITLE_LENGTH } from '@zip2live/shared';
import { PencilIcon } from './Icons.tsx';

export const TITLE_PLACEHOLDER = 'What’s this stream about?';

/** The title field before going live (Go Live) and in the editor below. */
export function TitleInput({ value, onChange, tone, label = 'Stream title', className = '', inputRef, onKeyDown }: {
  value: string;
  onChange: (v: string) => void;
  tone: 'light' | 'dark';
  label?: string;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  const colors = tone === 'dark'
    ? 'border-white/20 bg-white/10 text-white placeholder:text-white/55 focus:border-white'
    : 'border-line bg-white text-navy placeholder:text-muted/70 focus:border-navy';
  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      maxLength={MAX_TITLE_LENGTH}
      aria-label={label}
      placeholder={TITLE_PLACEHOLDER}
      enterKeyHint="done"
      autoComplete="off"
      // 16 px: iPhones zoom into smaller fields.
      className={`h-12 w-full min-w-0 rounded-xl border-2 px-3.5 text-[16px] font-semibold outline-none ${colors} ${className}`}
    />
  );
}

/**
 * A stream's title with an Edit button. Editing swaps in a field: Enter or Save stores it (empty
 * clears it), Escape or Cancel leaves it as it was. `display` replaces the plain title text (e.g.
 * with a link to the stream).
 */
export function TitleEditor({ title, onSave, tone = 'light', display, empty = 'No title', className = '', label = 'Stream title', compact = false }: {
  title: string | null;
  onSave: (title: string | null) => Promise<unknown>;
  tone?: 'light' | 'dark';
  display?: ReactNode;
  empty?: string;
  className?: string;
  label?: string;
  /** Pencil only, no word (tight rows). */
  compact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) field.current?.focus();
  }, [editing]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave(draft.trim() || null);
      setEditing(false);
    } catch (err) {
      setError((err as Error).message || 'Couldn’t save the title. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const muted = tone === 'dark' ? 'text-muted-dark' : 'text-muted';
  if (!editing) {
    return (
      <div className={`flex min-w-0 items-center gap-2 ${className}`}>
        {display ?? (title ? <span className="min-w-0 truncate font-extrabold">{title}</span> : <span className={`min-w-0 truncate ${muted}`}>{empty}</span>)}
        <button
          type="button"
          onClick={() => {
            setDraft(title ?? '');
            setError(null);
            setEditing(true);
          }}
          aria-label={title ? 'Edit title' : 'Add a title'}
          className={`inline-flex h-8 shrink-0 items-center gap-1 rounded-lg px-2 text-sm font-bold ${tone === 'dark' ? 'text-muted-dark hover:bg-white/10 hover:text-white' : 'text-muted hover:bg-mist hover:text-navy'}`}
        >
          <PencilIcon />
          {!compact && (title ? 'Edit' : 'Add title')}
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={(e) => void save(e)} className={`min-w-0 ${className}`}>
      <TitleInput
        value={draft}
        onChange={setDraft}
        tone={tone}
        label={label}
        inputRef={field}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <div className="mt-2 flex gap-2">
        <button type="submit" disabled={busy} className={`h-10 rounded-lg px-4 text-sm font-extrabold disabled:opacity-50 ${tone === 'dark' ? 'bg-white text-navy hover:bg-mist' : 'bg-navy text-white hover:bg-navy-700'}`}>
          {busy ? 'Saving…' : 'Save title'}
        </button>
        <button type="button" onClick={() => setEditing(false)} className={`h-10 rounded-lg px-3 text-sm font-bold ${muted} hover:underline`}>
          Cancel
        </button>
      </div>
      {error && <p role="alert" className={`mt-1.5 text-sm font-bold ${tone === 'dark' ? 'text-race-300' : 'text-race'}`}>{error}</p>}
    </form>
  );
}
